/**
 * Générateur — Automatisme : relations entre unités d'aire (1 m² = 100 dm²)
 * Attendu ID : 1028 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait que
 * 1 m² = 100 dm² et 1 dm² = 100 cm². »
 * Labels (cartes exerciseur) :
 *   - Relations entre unités d'aire (1 m² = 100 dm²)
 *
 * ── Plan de cartes (2026-08-21, cadrage prof) ──────────────────────────────
 * PÉRIMÈTRE STRICT : les DEUX égalités, rien d'autre — le saut m² → cm²
 * (10 000) est EXCLU (il se fait en deux étapes, hors automatisme).
 * Le 100 vient du PAVAGE (10 rangées de 10), jamais d'une manipulation
 * d'écriture « 10 dm × 10 dm = 10 × 10 dm² » (illisible en 6e, cadrage).
 * L'ERREUR CIBLE : transposer les longueurs (1 dm = 10 cm donc
 * « 1 dm² = 10 cm² ») — elle résiste aux règles, seul le comptage la corrige.
 * TROIS formats sur un domaine fermé assumé :
 *   - QCM : l'égalité, ou le PAVAGE (« combien de carrés de 1 dm pour paver
 *     un carré de 1 m ? ») ; grille {100 ; 10 ; 1 000 ; 20 = 10 + 10} ;
 *   - V/F : pool fermé étiqueté à la main (dont la mauvaise paire
 *     « 1 m² = 100 cm² ») ;
 *   - TROUS saisie : égalités sèches, ou pavage VERBALISÉ (10 rangées de
 *     10 → 100).
 */
import {
    randomChoice, shuffle, tirageSansRemise,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1028;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Relations entre unités d\'aire (1 m² = 100 dm²)';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1028 : ${message}`);
};

/** Les DEUX relations du domaine (fermé). */
const RELATIONS = [
    { grande: 'm²', petite: 'dm²', lonG: 'm', lonP: 'dm' },
    { grande: 'dm²', petite: 'cm²', lonG: 'dm', lonP: 'cm' },
];

// ─── Format 1 : QCM (l'égalité, le pavage... ou l'UNITÉ en réponse) ───────

/** 1/3 des tirages (demande prof 2026-08-22) : la réponse n'est plus 100
 *  mais l'UNITÉ manquante — casse le réflexe « c'est toujours 100 ».
 *  Distracteurs : la mauvaise paire (saut d'un cran), l'oubli du ² (le lien
 *  avec le 1026 « le ² se lit carré »), la recopie de l'unité de départ. */
const genQcmUnite = (r) => {
    const inverse = Math.random() < 0.5;
    const sousType = inverse ? 'unite_inverse' : 'unite';
    const texte = inverse
        ? `<p><b>Complète : 100 ${r.petite} = 1 ...</b></p>`
        : `<p><b>Complète : 1 ${r.grande} = 100 ...</b></p>`;
    const bonne = inverse ? r.grande : r.petite;
    const dis = inverse
        ? [
            { valeur: r.petite, code: 'recopie_unite' },
            { valeur: r.lonG, code: 'oubli_du_carre' },
            { valeur: r.grande === 'm²' ? 'cm²' : 'm²', code: 'mauvaise_paire' },
        ]
        : [
            { valeur: r.grande === 'm²' ? 'cm²' : 'mm²', code: 'mauvaise_paire' },
            { valeur: r.lonP, code: 'oubli_du_carre' },
            { valeur: r.grande, code: 'recopie_unite' },
        ];
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, relation: r.grande, distracteurs: dis };
    return payload;
};

const genQcm = () => {
    const r = randomChoice(RELATIONS);
    if (Math.random() < 1 / 3) return genQcmUnite(r);
    const sousType = randomChoice(['egalite', 'egalite_inverse', 'question', 'pavage']);
    const texte = {
        egalite: `<p><b>Complète : 1 ${r.grande} = ... ${r.petite}</b></p>`,
        egalite_inverse: `<p><b>Complète : ... ${r.petite} = 1 ${r.grande}</b></p>`,
        question: `<p><b>1 ${r.grande}, c'est combien de ${r.petite} ?</b></p>`,
        pavage: `<p><b>Combien faut-il de carrés de 1 ${r.lonP} de côté pour paver un carré de 1 ${r.lonG} de côté ?</b></p>`,
    }[sousType];
    // l'erreur cible (10) est TOUJOURS là ; les deux autres pièges tournent
    const seconds = shuffle([
        { valeur: '1 000', code: 'rang_de_trop' },
        { valeur: '20', code: 'addition_des_dix' },
        { valeur: '10 000', code: 'saut_double' },
    ]).slice(0, 2);
    const dis = [{ valeur: '10', code: 'transposition_longueur' }, ...seconds];
    const payload = makeQcm(
        ['100', ...dis.map((c) => c.valeur)],
        '100',
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, relation: r.grande, distracteurs: dis };
    return payload;
};

// ─── Format 2 : V/F (pool fermé étiqueté à la main) ───────────────────────

const AFFIRMATIONS = [
    { t: '1 m² = 100 dm²', v: true },
    { t: '1 dm² = 100 cm²', v: true },
    { t: '100 cm² = 1 dm²', v: true },
    { t: '100 dm² = 1 m²', v: true },
    { t: '1 dm² = 10 cm²', v: false },                  // L'ERREUR CIBLE
    { t: '1 m² = 10 dm²', v: false },
    { t: '1 m² = 100 cm²', v: false },                  // la mauvaise paire
    { t: '1 m² = 1 000 dm²', v: false },
    { t: '10 cm² = 1 dm²', v: false },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [AFFIRMATIONS[0].t, AFFIRMATIONS[4].t, AFFIRMATIONS[1].t, AFFIRMATIONS[6].t],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 3 : trous saisie (égalités sèches, ou pavage verbalisé) ───────

/** Une égalité à un trou, deux sens d'écriture, et dans 1/3 des cas le trou
 *  porte sur le 1 (demande prof 2026-08-22 : « 100 dm², c'est exactement UN
 *  m² » — la réponse n'est plus toujours 100). */
const egaliteTrou = (r) => {
    const trouSurUn = Math.random() < 1 / 3;
    if (trouSurUn) {
        return Math.random() < 0.5
            ? `{{1}} ${r.grande} = 100 ${r.petite}`
            : `100 ${r.petite} = {{1}} ${r.grande}`;
    }
    return Math.random() < 0.5
        ? `1 ${r.grande} = {{100}} ${r.petite}`
        : `{{100}} ${r.petite} = 1 ${r.grande}`;
};

const genTrouSaisie = () => {
    const variante = randomChoice(['egalites', 'egalites', 'pavage', 'comptage']);
    if (variante === 'egalites') {
        // une ou deux égalités, sens variés, une par ligne
        const choisies = shuffle([...RELATIONS]).slice(0, randomChoice([1, 2, 2]));
        const lignes = choisies.map((r) => egaliteTrou(r));
        return makeTexteTrou(
            lignes.join('\n'),
            '<p><b>Complète les égalités.</b></p>',
            { ...opts(L1), mode: 'saisie' },
        );
    }
    const r = randomChoice(RELATIONS);
    // la finale s'écrit dans les deux sens, trou sur 100 ou sur le 1
    const finale = egaliteTrou(r);
    if (variante === 'comptage') {
        // le pavage raconté depuis le comptage (gabarit du cadrage)
        const phraseC = `Dans un carré de 1 ${r.lonG} de côté, on compte {{10}} rangées de {{10}} carrés `
            + `de 1 ${r.lonP} : il y a {{100}} carrés, donc\n${finale}`;
        return makeTexteTrou(
            phraseC,
            '<p><b>Complète le comptage.</b></p>',
            { ...opts(L1), mode: 'saisie' },
        );
    }
    // le pavage verbalisé : le 100 vient de 10 rangées de 10
    const phrase = `Sur un côté de 1 ${r.lonG}, on pose {{10}} ${r.lonP} ; dans le carré, `
        + `{{10}} rangées de {{10}} carrés :\n${finale}`;
    garde(!phrase.includes('×'), 'pas de manipulation d\'écriture');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète le pavage.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'PÉRIMÈTRE STRICT : les deux égalités seulement — jamais m² → cm² (10 000, deux étapes, hors automatisme)',
        'le 100 vient du PAVAGE (10 rangées de 10), jamais d\'une manipulation d\'unités',
        'domaine fermé assumé : variété par sous-types, sens et formulations',
    ],
    conv_aires_qcm_unique: {
        support: 'l\'égalité ou le pavage, sur les deux relations',
        fixes: {
            transposition_longueur: '10 — L\'ERREUR CIBLE : transposer 1 dm = 10 cm aux aires',
            rang_de_trop: '1 000',
            addition_des_dix: '20 = 10 + 10 : additionner les deux dimensions au lieu de les multiplier',
        },
        rotation_unite: {
            part: '1/3 des tirages (prof 2026-08-22) : l\'UNITÉ manquante est la réponse',
            fixes: {
                mauvaise_paire: 'l\'unité un cran trop loin (1 m² = 100 cm², 100 cm² = 1 m²...)',
                oubli_du_carre: 'l\'unité de LONGUEUR (dm au lieu de dm²) — lien avec le 1026',
                recopie_unite: 'l\'unité déjà écrite dans l\'égalité',
            },
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    conv_aires_qcm_unique: genQcm,
    conv_aires_tableau_vf: genVraiFaux,
    conv_aires_texte_trou_saisie: genTrouSaisie,
};

const FORMAT_LABELS = {
    conv_aires_qcm_unique: 'QCM',
    conv_aires_tableau_vf: 'Vrai/Faux',
    conv_aires_texte_trou_saisie: 'Saisie',
};

export const CATEGORIES = [
    {
        id: 'convaires',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // domaine minuscule (2 relations) : TIRAGE SANS REMISE par format —
    // toutes les questions uniques sortent avant qu'une seule ne revienne
    // (helper tirageSansRemise, prof 2026-08-21). Signature = la QUESTION
    // (énoncé + phrase/lignes) ; les grilles de distracteurs restent libres.
    const payload = tirageSansRemise(
        `gen_1028:${key}`,
        () => GENERATORS[key](),
        (p) => p.texte + JSON.stringify(p.contenu.phrase
            || (p.elements_additionnels?.elements?.[0]?.config?.lignes ?? '')),
        250,     // combinaisons saisie rares : fin de cycle plus insistante
    );
    payload._meta.generatorKey = key;
    return payload;
};
